import * as FieldValidation from './shared/field-validation.js';
import { handleSpaContentAuthError } from './shared/spa-auth.js';
import { resolveShowAlertMsg } from './shared/resolve-show-alert-msg.js';
import { cleanupBootstrapModalBody } from './shared/bootstrap-modal-cleanup.js';
import { markSettingsFormsClean } from './manager/settings-unsaved-guard.js';
import { getManagerOverlayElement, setOverlayLabel, setOverlayProgress, showOverlay, hideOverlay } from './shared/overlay.js';
import { getManagerNavAction } from './manager/manager-nav-registry.js';
import { getMemberNavAction } from './members/member-nav-registry.js';
import './shared/disable-number-input-wheel.js';

const AJAX_ERROR_MESSAGE = 'An unknown error occurred, please try again';
const SUCCESS_TOAST_OVERLAY_WAIT_MS = 4000;
const SUCCESS_TOAST_OVERLAY_POLL_MS = 100;

export const AJAX_TIMEOUT_MESSAGE = 'A time out error occurred, please try again';

export function createSiteToastApi(opts) {
    const options = opts || {};
    const durationMs = Number(options.durationMs || 4000);
    const stackId = String(options.stackId || 'site-toast-stack');
    const resizeNamespace = String(options.resizeNamespace || 'siteToastPlacement');

    function placeStack(container) {
        let topOffset = 12;
        const headers = document.querySelectorAll('.topBar, .topMemberBar, .fixed-top');
        for (let i = 0; i < headers.length; i++) {
            const header = headers[i];
            if (!header || header.offsetParent === null) continue;
            const rect = header.getBoundingClientRect();
            if (!rect || rect.height <= 0) continue;
            topOffset = Math.max(topOffset, Math.ceil(rect.bottom) + 12);
        }
        container.style.setProperty('--site-toast-top', topOffset + 'px');
    }

    function syncPlacement() {
        const stack = document.getElementById(stackId);
        if (!stack) return;
        placeStack(stack);
    }

    function getStack() {
        const existing = document.getElementById(stackId);
        if (existing) {
            placeStack(existing);
            return existing;
        }
        const container = document.createElement('div');
        container.id = stackId;
        container.className = 'site-toast-stack';
        placeStack(container);
        document.body.appendChild(container);
        window.jQuery(window).off('resize.' + resizeNamespace).on('resize.' + resizeNamespace, syncPlacement);
        return container;
    }

    function templateId(type) {
        if (type === 'warning') return 'site-toast-warning-template';
        if (type === 'danger') return 'site-toast-danger-template';
        if (type === 'info') return 'site-toast-info-template';
        return 'site-toast-success-template';
    }

    return function showToast(msg, type) {
        const message = (Array.isArray(msg) ? msg.join('. ') : String(msg == null ? '' : msg)).trim();
        if (!message) return;
        const resolvedType = type || 'success';
        const template = document.getElementById(templateId(resolvedType));
        if (!template) return;
        const clone = template.content.cloneNode(true);
        const toastNode = clone.querySelector('.site-toast');
        if (!toastNode) return;
        toastNode.querySelector('.site-toast__msg').textContent = message;
        getStack().appendChild(clone);
        const $toast = window.jQuery(toastNode);
        const dismissTimer = window.setTimeout(function () {
            if ($toast.data('isClosing')) return;
            $toast.data('isClosing', true);
            $toast.stop(true, true).slideUp(180, function () {
                window.jQuery(this).remove();
            });
        }, durationMs);
        $toast.one('close.bs.alert', function () {
            window.clearTimeout(dismissTimer);
        });
    };
}

export function parseNamedCallbackConfig(callbackConfig) {
    const raw = String(callbackConfig || '').trim();
    if (raw === '') {
        return null;
    }
    const callbackText = raw.startsWith('window.') ? raw.slice('window.'.length) : raw;
    const openParenIndex = callbackText.indexOf('(');
    const closeParenIndex = callbackText.lastIndexOf(')');

    if (openParenIndex <= 0 || closeParenIndex !== callbackText.length - 1) {
        return null;
    }

    const functionName = callbackText.slice(0, openParenIndex).trim();
    if (!/^[A-Za-z_$][\w$]*$/.test(functionName)) {
        return null;
    }

    const argsText = callbackText.slice(openParenIndex + 1, closeParenIndex).trim();
    const passEvent = argsText === 'event';
    if (argsText !== '' && !passEvent) {
        return null;
    }

    return {
        functionName: functionName,
        passEvent: passEvent,
    };
}

function resolveNamedCallback(functionName) {
    try {
        return getManagerNavAction(functionName);
    } catch {
        try {
            return getMemberNavAction(functionName);
        } catch {
            return null;
        }
    }
}

export function runNamedCallback(callbackConfig, event) {
    const parsed = parseNamedCallbackConfig(callbackConfig);
    if (!parsed) {
        console.error('runNamedCallback: could not parse callback config', callbackConfig);
        return;
    }
    const callback = resolveNamedCallback(parsed.functionName);
    if (typeof callback !== 'function') {
        console.error('runNamedCallback: no registered nav action named', parsed.functionName);
        return;
    }
    try {
        if (parsed.passEvent) {
            callback(event);
            return;
        }
        callback();
    } catch (error) {
        console.error(error);
    }
}

function isDatabaseExceptionMessage(message) {
    return /SQLSTATE\[|\(SQL:/i.test(message);
}

function readAjaxErrorText(message) {
    if (typeof message !== 'string') return '';
    const text = message.trim();
    if (text === '') return '';
    if (isDatabaseExceptionMessage(text)) return AJAX_ERROR_MESSAGE;
    return text;
}

export function resolveAjaxErrorMessage(xhr, fallbackMessage) {
    const fallback = fallbackMessage || AJAX_ERROR_MESSAGE;
    if (!xhr) return fallback;
    let payload = null;
    if (xhr.responseJSON && typeof xhr.responseJSON === 'object') {
        payload = xhr.responseJSON;
    } else if (typeof xhr.responseText === 'string' && xhr.responseText.trim() !== '') {
        try {
            const parsed = JSON.parse(xhr.responseText);
            if (parsed && typeof parsed === 'object') payload = parsed;
        } catch (_error) {
            payload = null;
        }
    }
    const errorText = readAjaxErrorText(payload && payload.error);
    if (errorText) return errorText;
    if (payload && payload.errors && typeof payload.errors === 'object') {
        const parts = [];
        let hasDatabaseError = false;
        Object.keys(payload.errors).forEach(function (key) {
            const fieldErrors = payload.errors[key];
            const messages = Array.isArray(fieldErrors) ? fieldErrors : [fieldErrors];
            messages.forEach(function (msg) {
                if (typeof msg === 'string' && isDatabaseExceptionMessage(msg)) {
                    hasDatabaseError = true;
                    return;
                }
                const text = readAjaxErrorText(msg);
                if (text) parts.push(text);
            });
        });
        if (hasDatabaseError) return AJAX_ERROR_MESSAGE;
        if (parts.length) return parts.join(' ');
    }
    const messageText = readAjaxErrorText(payload && payload.message);
    if (messageText) return messageText;
    return fallback;
}

function notifyAlert(message, type) {
    const fn = resolveShowAlertMsg();
    if (typeof fn === 'function') {
        fn(message, type);
        return;
    }
    if (typeof window.showAlertMsg === 'function') {
        window.showAlertMsg(message, type);
        return;
    }
    if (type === 'danger' && message) {
        window.alert(message);
    }
}

function showAlertAfterOverlay(msg) {
    if (!msg) return;
    const startedAt = Date.now();
    const tryShow = function () {
        const elapsed = Date.now() - startedAt;
        const overlay = getManagerOverlayElement();
        const overlayVisible = !!overlay && getComputedStyle(overlay).display !== 'none';
        if (!overlayVisible || elapsed >= SUCCESS_TOAST_OVERLAY_WAIT_MS) {
            notifyAlert(msg);
            return;
        }
        window.setTimeout(tryShow, SUCCESS_TOAST_OVERLAY_POLL_MS);
    };
    tryShow();
}

/**
 * @param {{ modal?: string, overlay?: boolean, message?: string, xhr?: any, redirect?: function(): void, loginUrl?: string }} opts
 * @returns {void}
 */
export function handleAjaxError(opts) {
    const o = opts || {};
    if (o.modal && window.jQuery) {
        window.jQuery(o.modal).modal('hide');
        cleanupBootstrapModalBody();
    }
    if (o.overlay) hideOverlay(getManagerOverlayElement());

    if (handleSpaContentAuthError(o.xhr, {
        loginUrl: o.loginUrl,
        alert: resolveShowAlertMsg(),
        onForbidden: function () {
            if (typeof o.redirect === 'function') {
                o.redirect();
                return;
            }
            try {
                getManagerNavAction('openMgrIndexPage')();
            } catch (err) {
                // Not in manager shell (e.g. member/super).
            }
        },
    })) {
        return;
    }

    let message = o.message || '';
    if (!message) message = resolveAjaxErrorMessage(o.xhr, AJAX_ERROR_MESSAGE);
    notifyAlert(message || AJAX_ERROR_MESSAGE, 'danger');
    const isValidationError = !!(o.xhr && o.xhr.status === 422);
    if (!isValidationError && typeof o.redirect === 'function') o.redirect();
}

/**
 * @param {{ success: boolean, error?: string, message?: string, msg?: string }} response
 * @param {{ modal?: string, overlay?: boolean, redirect?: function(): void, alertMessage?: string }} opts
 * @returns {void}
 */
export function handleAjaxSuccess(response, opts) {
    const o = opts || {};
    if (o.modal && window.jQuery) {
        window.jQuery(o.modal).modal('hide');
        cleanupBootstrapModalBody();
    }
    if (o.overlay) hideOverlay(getManagerOverlayElement());
    if (!response.success) {
        notifyAlert(response.error || AJAX_ERROR_MESSAGE, 'danger');
        return;
    }
    if (document.querySelector('#mgr_index_paging .settings-manager-page')) {
        markSettingsFormsClean();
    }
    const msg = o.alertMessage || response.message || response.msg;
    if (typeof o.redirect === 'function') {
        o.redirect();
        showAlertAfterOverlay(msg);
        return;
    }
    if (msg) notifyAlert(msg);
}

/** @typedef {{ success: boolean, data?: Object, error?: string }} PledgeModalResponse */

/**
 * @param {string} pledgeId
 * @param {string} routeTemplate
 * @returns {Promise<PledgeModalResponse>}
 */
export function fetchPledgeModalValues(pledgeId, routeTemplate) {
    const url = (routeTemplate || '')
        .replace(':pledge_id', pledgeId)
        .replace(':id', pledgeId);
    if (!url) return Promise.resolve({ success: false });
    return new Promise(function (resolve) {
        window.jQuery.ajax({
            url: url,
            type: 'GET',
            headers: { 'X-CSRF-TOKEN': (window.jQuery('meta[name="csrf-token"]').attr('content') || '') },
            success: function (response) { resolve(response || { success: false }); },
            error: function () { resolve({ success: false }); }
        });
    });
}

/** @param {string} str @returns {string} */
export function escapeHtml(str) {
    if (str == null) return '';
    const s = String(str);
    return s
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}

export function isDirty($input, dataType = 'number') {
    if (!$input || !$input.length) {
        return false;
    }
    const rawValue = $input.is('select') ? $input.find('option:selected').val() : $input.val();
    const rawInitial = $input.data('initial');

    if (dataType === 'string') {
        const currentValue = (rawValue ?? '').toString().trim();
        const initialValue = (rawInitial ?? '').toString().trim();
        return currentValue !== '' && currentValue !== initialValue;
    }

    const normalizeNumber = (value) => {
        if (value === null || value === undefined) {
            return NaN;
        }
        if (typeof value === 'string') {
            value = value.replace(/,/g, '').trim();
        }
        return parseFloat(value);
    };

    const currentNumber = normalizeNumber(rawValue);
    const initialNumber = normalizeNumber(rawInitial);
    if (Number.isNaN(currentNumber)) {
        return false;
    }
    if (Number.isNaN(initialNumber)) {
        return currentNumber > 0;
    }
    return currentNumber > 0 && currentNumber !== initialNumber;
}

export function restrictAmountInput(el) {
    const rawValue = String(el.value || '');
    const digitsAndDotOnly = rawValue.replace(/[^\d.]/g, '');
    const amountWithPrecision = digitsAndDotOnly.replace(/^(\d{0,5})(\.\d{0,2})?.*/, '$1$2');
    el.value = amountWithPrecision;
}

export function applyPhoneMask($container) {
    const $scope = window.jQuery($container || document);
    const $phones = $scope.is('.phone') ? $scope : $scope.find('.phone');

    FieldValidation.applyPhoneMask($phones);
}

export function getRootCssVar(name) {
    if (!name) return '';
    return (getComputedStyle(document.documentElement).getPropertyValue(name) || '').trim();
}

export function setIconLabelButtonContent(buttonOrSelector, iconClasses, labelText) {
    const $button = window.jQuery(buttonOrSelector);
    if (!$button.length) return;
    $button.empty();
    $button.append(window.jQuery('<i>').addClass(iconClasses || ''));
    $button.append(document.createTextNode(' ' + String(labelText == null ? '' : labelText)));
}

function moveOverlayToBody() {
    const overlay = document.getElementById('overlay');
    if (!overlay || overlay.parentElement === document.body) return;
    document.body.appendChild(overlay);
}

function handleLoginFormSubmit(event) {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    const button = form.querySelector('.js-login-submit-btn');
    if (!button || button.disabled) return;
    button.dataset.originalText = button.textContent;
    button.disabled = true;
    button.textContent = 'Logging in...';
}

export function resetLoginSubmitButton(form) {
    const button = form.querySelector('.js-login-submit-btn');
    if (!button) return;
    button.disabled = false;
    button.textContent = button.dataset.originalText || button.textContent;
}

function handleLoginLinkClick(event) {
    const link = event.target.closest('.js-login-link');
    if (!link || link.dataset.loggingIn) return;
    link.dataset.loggingIn = '1';
    const label = link.querySelector('.js-login-link-label');
    if (!label) throw new Error('js-login-link is missing a .js-login-link-label element');
    label.textContent = 'Logging in...';
}

function bindGlobalListeners() {
    document.addEventListener('submit', handleLoginFormSubmit);
    document.addEventListener('click', handleLoginLinkClick);
}

function runOnDomReady() {
    FieldValidation.initGlobalFieldValidation();
    moveOverlayToBody();
    bindGlobalListeners();
}

export function init() {
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', runOnDomReady, { once: true });
        return;
    }
    runOnDomReady();
}

export function getIconLabelButtonContent(buttonOrSelector) {
    const $button = window.jQuery(buttonOrSelector);
    if (!$button.length) {
        return { iconClass: '', buttonText: '' };
    }
    const $icon = $button.find('i').first();
    return {
        iconClass: $icon.length ? ($icon.attr('class') || '') : '',
        buttonText: $button.text().trim()
    };
}

export function setButtonPendingState(buttonOrSelector, pendingLabel) {
    const $button = window.jQuery(buttonOrSelector);
    if (!$button.length) {
        return null;
    }
    const snapshot = {
        disabled: $button.prop('disabled'),
        text: $button.is('input') ? $button.val() : $button.text(),
    };
    $button.prop('disabled', true);
    const label = String(pendingLabel || '').trim();
    if (label !== '' && $button.is('button, input[type="button"], input[type="submit"]')) {
        if ($button.is('input')) {
            $button.val(label);
        } else {
            $button.text(label);
        }
    }
    return snapshot;
}

export function restoreButtonPendingState(buttonOrSelector, snapshot) {
    const $button = window.jQuery(buttonOrSelector);
    if (!$button.length || !snapshot) {
        return;
    }
    $button.prop('disabled', !!snapshot.disabled);
    if (!$button.is('button, input[type="button"], input[type="submit"]')) {
        return;
    }
    if ($button.is('input')) {
        $button.val(String(snapshot.text == null ? '' : snapshot.text));
    } else {
        $button.text(String(snapshot.text == null ? '' : snapshot.text));
    }
}

export function showManagerOverlay() {
    showOverlay(getManagerOverlayElement());
}

export function hideManagerOverlay() {
    const overlay = getManagerOverlayElement();
    hideOverlay(overlay);
    setOverlayLabel(overlay, DEFAULT_OVERLAY_LABEL);
}

export function setManagerOverlayProgress(percent, label) {
    const overlay = getManagerOverlayElement();
    setOverlayProgress(overlay, percent);
    setOverlayLabel(overlay, label);
}

const DEFAULT_OVERLAY_LABEL = 'Loading...';

export function withOverlayLoader(ajaxOpts) {
    if (!window.jQuery) return null;
    const $ = window.jQuery;
    const overlayEl = getManagerOverlayElement();
    const deferHide = !!ajaxOpts.deferHide;
    const showImmediately = !!ajaxOpts.showOverlayImmediately || deferHide;
    const minOverlayMs = Number(ajaxOpts.minOverlayMs) || 0;

    function hideOverlayNow() {
        hideOverlay(overlayEl);
        setOverlayLabel(overlayEl, DEFAULT_OVERLAY_LABEL);
    }

    setOverlayLabel(overlayEl, ajaxOpts.overlayLabel || DEFAULT_OVERLAY_LABEL);
    showOverlay(overlayEl, { immediate: showImmediately, minVisibleMs: minOverlayMs });

    const originalSuccess = ajaxOpts.success;
    const originalError = ajaxOpts.error;
    const originalComplete = ajaxOpts.complete || (() => {});

    function finishOverlay() {
        if (!deferHide) hideOverlayNow();
    }

    const xhr = $.ajax({
        ...ajaxOpts,
        deferHide: undefined,
        showOverlayImmediately: undefined,
        overlayLabel: undefined,
        minOverlayMs: undefined,
        success: function (...args) {
            finishOverlay();
            if (typeof originalSuccess === 'function') originalSuccess.apply(this, args);
        },
        error: function (...args) {
            finishOverlay();
            if (typeof originalError === 'function') originalError.apply(this, args);
        },
        complete: originalComplete
    });

    return deferHide ? { xhr, hide: hideOverlayNow } : xhr;
}

export function matchFaIconFromTitle(title, matchRules) {
    const haystack = String(title || '')
        .toLowerCase()
        .replace(/\s+/g, ' ')
        .trim();
    const rules = Array.isArray(matchRules) ? matchRules : [];

    if (!haystack) {
        return null;
    }

    for (let i = 0; i < rules.length; i++) {
        const rule = rules[i];
        if (!rule || !rule.icon) {
            continue;
        }
        if (rule.type === 'contains' && Array.isArray(rule.needles)) {
            for (let j = 0; j < rule.needles.length; j++) {
                const needle = String(rule.needles[j] || '').toLowerCase();
                if (needle && haystack.indexOf(needle) !== -1) {
                    return rule.icon;
                }
            }
        }
        if (rule.type === 'regex' && rule.pattern) {
            let regex;
            try {
                const pattern = String(rule.pattern);
                if (pattern.charAt(0) === '/') {
                    const lastSlash = pattern.lastIndexOf('/');
                    regex = new RegExp(pattern.slice(1, lastSlash), pattern.slice(lastSlash + 1));
                } else {
                    regex = new RegExp(pattern, 'i');
                }
            } catch (e) {
                continue;
            }
            if (regex.test(haystack)) {
                return rule.icon;
            }
        }
    }

    return null;
}

export function initCampaignIconPicker(opts) {
    const {
        triggerSelector,
        inputSelector,
        rowSelector,
        modalSelector,
        gridSelector,
        iconOptions,
        matchRules,
        defaultIcon,
        titleSelector,
        generateSelector,
        onChange
    } = opts;
    let currentButton = null;
    const generateBtnSelector = generateSelector || '.fa-icon-generate-btn';
    const defaultTitleSelector = titleSelector || '';
    const eventNs = 'faIconPicker' + String(modalSelector || '').replace(/[^a-zA-Z0-9]/g, '');

    const normalizedOptions = iconOptions.map(function (item) {
        return { icon: item.icon, label: item.label };
    });

    function labelFor(iconClass) {
        const match = normalizedOptions.find(function (opt) { return opt.icon === iconClass; });
        return match ? match.label : 'Choose Icon';
    }

    function resolveRow($el) {
        return $el.closest(rowSelector);
    }

    function resolveInput($button) {
        return resolveRow($button).find(inputSelector).not('.campaign-mobile-duplicate').first();
    }

    function resolvePrimaryButton($row) {
        return $row.find(triggerSelector).not('.campaign-mobile-duplicate').first();
    }

    function resolveTitle($row, $group) {
        const fromGroup = ($group && $group.attr('data-title-selector')) || '';
        const selector = fromGroup || defaultTitleSelector;
        if (!selector) {
            return '';
        }
        return ($row.find(selector + ':not(:disabled)').first().val() || '').trim();
    }

    function setButtonDisplay($button, iconClass) {
        $button.attr('data-selected-icon', iconClass);
        $button.find('.campaign-icon-picker-btn-icon i').attr('class', iconClass || '');
        $button.find('.campaign-icon-picker-btn-label').text(labelFor(iconClass));
    }

    function syncWandVisibility($row) {
        const icon = ($row.find(inputSelector).not('.campaign-mobile-duplicate').first().val() || '').trim();
        $row.find(generateBtnSelector).toggleClass('icon-wand-hidden', !!icon);
    }

    function applyIconToRow($row, iconClass, options) {
        const optsLocal = options || {};
        $row.find(inputSelector).val(iconClass || '');
        $row.find(triggerSelector).each(function () {
            setButtonDisplay(window.jQuery(this), iconClass || '');
        });
        if (optsLocal.markDirty) {
            resolvePrimaryButton($row).attr('data-icon-dirty', '1');
        }
        if (onChange) {
            onChange($row, iconClass || '');
        }
    }

    function buildOptionsHtml(selectedIcon) {
        return normalizedOptions.map(function (opt) {
            const isActive = opt.icon === selectedIcon ? ' is-active' : '';
            return '<button type="button" class="campaign-icon-option' + isActive + '" data-icon="' + escapeHtml(opt.icon) + '">' +
                '<span class="campaign-icon-option-preview"><i class="' + escapeHtml(opt.icon) + '"></i></span>' +
                '<span class="campaign-icon-option-label">' + escapeHtml(opt.label) + '</span>' +
                '</button>';
        }).join('');
    }

    function getModal() {
        return window.jQuery(modalSelector);
    }

    function getGrid() {
        return window.jQuery(gridSelector);
    }

    function syncButtons(scope) {
        const $scope = scope;
        $scope.find(triggerSelector).each(function () {
            const $button = window.jQuery(this);
            const $input = resolveInput($button);
            const icon = ($input.val() || '').trim();
            if (icon) {
                setButtonDisplay($button, icon);
            }
        });
        $scope.find(rowSelector).each(function () {
            syncWandVisibility(window.jQuery(this));
        });
    }

    if (defaultTitleSelector) {
        window.jQuery(document).off('input.' + eventNs + 'Title change.' + eventNs + 'Title', defaultTitleSelector)
            .on('input.' + eventNs + 'Title change.' + eventNs + 'Title', defaultTitleSelector, function () {
                resolveRow(window.jQuery(this)).find(generateBtnSelector).removeClass('icon-wand-hidden');
            });
    }

    window.jQuery(document).off('click.' + eventNs, triggerSelector).on('click.' + eventNs, triggerSelector, function (event) {
        event.preventDefault();
        const $button = window.jQuery(this);
        if ($button.is(':disabled')) return;
        if (!resolveRow($button).length) return;
        currentButton = $button;
        const selected = $button.attr('data-selected-icon') || resolveInput($button).val();
        getGrid().html(buildOptionsHtml(selected));
        getModal().modal('show');
    });

    window.jQuery(document).off('click.' + eventNs + 'Option', modalSelector + ' .campaign-icon-option')
        .on('click.' + eventNs + 'Option', modalSelector + ' .campaign-icon-option', function (event) {
            event.preventDefault();
            if (!currentButton || !currentButton.length) return;
            const selectedIcon = window.jQuery(this).data('icon');
            const $pickedRow = resolveRow(currentButton);
            applyIconToRow($pickedRow, selectedIcon, { markDirty: true });
            $pickedRow.find(generateBtnSelector).removeClass('icon-wand-hidden');
            getModal().modal('hide');
        });

    window.jQuery(document).off('hidden.bs.modal.' + eventNs, modalSelector)
        .on('hidden.bs.modal.' + eventNs, modalSelector, function () {
            currentButton = null;
            getGrid().empty();
        });

    window.jQuery(document).off('click.' + eventNs + 'Generate', generateBtnSelector)
        .on('click.' + eventNs + 'Generate', generateBtnSelector, function (event) {
            event.preventDefault();
            const $btn = window.jQuery(this);
            if ($btn.is(':disabled')) {
                return;
            }
            const $row = resolveRow($btn);
            if (!$row.length) {
                return;
            }
            const title = resolveTitle($row, $btn.closest('.fa-icon-picker-group'));
            const matched = matchFaIconFromTitle(title, matchRules);
            const iconToApply = matched || defaultIcon || '';

            const $displayButtons = $row.find(triggerSelector);
            $displayButtons.removeClass('ai-apply-pulse');
            void $displayButtons.get(0)?.offsetWidth;
            $displayButtons.addClass('ai-apply-pulse');
            window.setTimeout(function () {
                $displayButtons.removeClass('ai-apply-pulse');
            }, 1100);

            const $wandIcon = $btn.find('i');
            $wandIcon.removeClass('fa-wand-magic-sparkles').addClass('fa-check');
            window.setTimeout(function () {
                $wandIcon.removeClass('fa-check').addClass('fa-wand-magic-sparkles');
            }, 1500);

            window.setTimeout(function () {
                applyIconToRow($row, iconToApply, { markDirty: true });
                $row.find(generateBtnSelector).addClass('icon-wand-hidden');
                const $icons = $displayButtons.find('.campaign-icon-picker-btn-icon');
                $icons.removeClass('ai-pop-in');
                void $icons.get(0)?.offsetWidth;
                $icons.addClass('ai-pop-in');
            }, 700);
        });

    return {
        syncButtons: syncButtons,
    };
}
